import { useTranslation } from 'gatsby-plugin-react-i18next';
import React, { FC, useEffect } from 'react';
import useKeyPress from '../../hooks/useKeyPress';
import Checkmark from '../../images/checkmark.svg';
import { ALPHABETS } from '../../utils';
import FormContainer from '../Shared/FormContainer/FormContainer';
import { FORM_CHECKBOX_COMPONENT_TYPES } from './types';

import { ErrorMessage } from '../Shared/Shared';
import { Accessibility, Checkbox, FormCheckboxContianer, FormCheckboxLabel, FormCheckboxWrapper, FormItemInput, ItemContainer, Name } from './styled';

interface FormCheckboxProps {
  type: keyof typeof FORM_CHECKBOX_COMPONENT_TYPES;
  formOptions: Record<string, any>;
  onSubmit: (data: Record<string, string>) => void;
  checkboxData: { name: string; id: string; disabled?: boolean; pic?: string }[];
  children?: React.ReactNode;
  loading?: boolean;
  previousPage: Record<string, any>;
}
const FormCheckbox: FC<FormCheckboxProps> = ({ type, checkboxData, formOptions, onSubmit, children, loading = false, previousPage }) => {
  const { t } = useTranslation();
  const title = t(`formCheckbox.selectPets.title`);
  const requiredMessage = t(`formCheckbox.selectPets.requiredMessage`);

  const { register, handleSubmit, setValue, getValues, setFocus, setError, formState } = formOptions;

  const keys = useKeyPress(ALPHABETS.slice(0, checkboxData?.length));

  useEffect(() => {
    if (keys) {
      const index = ALPHABETS.findIndex(alphabet => keys[alphabet]);
      if (index !== -1) {
        const selected = getValues().checkbox[type] || [];
        const item = checkboxData[index];
        const selectedItems = selected.includes(item?.id) ? selected.filter((id: string) => id !== item?.id) : [...selected, item?.id];
        const itemNotDisabled = !item?.disabled;

        if (itemNotDisabled) {
          setValue(`checkbox.${type}`, selectedItems);
          setFocus(`checkbox.${type}`);
        }
      }
    }
  }, [keys]);

  const submitHandler = (data: Record<string, any>) => {
    if (!data.checkbox[type].length) {
      setError(`checkbox.${type}`, { requiredMessage });
      return;
    }
    onSubmit(data);
  };

  const watchedValues = formOptions.watch(`checkbox.${type}`) || [];

  return (
    <FormCheckboxWrapper>
      <FormContainer title={title} previousPage={previousPage} loadingData={loading} error={formState?.errors?.checkbox?.[type]?.requiredMessage} onSubmit={handleSubmit(submitHandler)}>
        <FormCheckboxContianer>
          {!!checkboxData?.length &&
            checkboxData.map((data, index) => {
              const { name, id, disabled, pic } = data;
              const isSelected = watchedValues.includes(id);
              const isDisabled = disabled && !isSelected;
              return (
                <ItemContainer key={id}>
                  <FormItemInput id={`checkbox-${id}`} {...register(`checkbox.${type}`)} type="checkbox" value={id} disabled={isDisabled} />
                  <FormCheckboxLabel disabled={isDisabled} htmlFor={`checkbox-${id}`}>
                    <Checkbox selected={isSelected} disabled={isDisabled}>
                      <Checkmark />
                    </Checkbox>
                    <img src={pic} alt={name} />
                    <Name>{name}</Name>
                  </FormCheckboxLabel>
                  <Accessibility>
                    {t('formCheckbox.accessibilityOrPress', 'or Press')}
                    <span> {ALPHABETS[index]}</span>
                  </Accessibility>
                </ItemContainer>
              );
            })}
          {children}
        </FormCheckboxContianer>

        {formState?.errors?.checkbox?.[type] && <ErrorMessage>{formState.errors.checkbox?.[type].requiredMessage}</ErrorMessage>}
      </FormContainer>
    </FormCheckboxWrapper>
  );
};

export default FormCheckbox;
